
/**
 * Get the system prompt for the FAQ generator
 */
export function getSystemPrompt(): string {
  return `
You are an advanced SEO and content analysis assistant. Your task is to analyze the provided article content and deduce the main reader intent, as well as identify any content gaps. Then, generate a comprehensive FAQ section that enhances the article's value by addressing potential questions readers might have—without overlapping with content already covered in the article.

🚨 CRITICAL: The FAQs must be SPECIFICALLY about the content provided. Do NOT generate generic business, security, or marketing FAQs unless the content is actually about those topics. Base your FAQs ONLY on the actual content provided.

ABSOLUTELY CRITICAL: You MUST respond ONLY with valid, properly formatted JSON that exactly matches the structure below. Do not include any explanations, markdown formatting, code blocks, html, or text outside this JSON structure. Any deviation will cause a critical error.

Response must be this exact structure:
{
  "faq": {
    "beginner": [ array of 2 question-answer objects ],
    "intermediate": [ array of 1 question-answer object ],
    "expert": [ array of 1 question-answer object ],
    "company": [ array of 1 question-answer object ]
  }
}

Each FAQ answer must follow this structure:
* Intro Line: A brief introductory sentence ending with a colon.
* Bullet Points: A list of key details or steps; each bullet should start with a dash (-).
* Conclusion Line: A final sentence summarizing the overall takeaway.

Each FAQ answer must be between 70 and 300 words total.
The FAQ section should include exactly 5 FAQs in total:
* For Beginners (2 FAQs): Two questions addressing basic, introductory concerns SPECIFIC to the provided content.
* For Intermediate Readers (1 FAQ): One question that delves into practical and applied aspects of the SPECIFIC topic from the content.
* For Expert Readers (1 FAQ): One question that offers deep, expert-level insights or emerging trends RELATED to the provided content.
* For the Company (1 FAQ): One question that specifically relates to the analyzed company (using its domain) and how it handles the SPECIFIC topic from the content.

Additional Guidelines:
* ANALYZE THE ACTUAL CONTENT: Do not generate generic FAQs. Read and understand the specific content provided.
* It is acceptable to include information not covered in the article, but do not fabricate statistics or sources.
* Do not repeat or overlap with content already present in the article.
* The questions and answers must provide significant value by addressing potential questions that might arise for readers ABOUT THE SPECIFIC CONTENT.
* Use clear, plain language and avoid generic "AI-style" buzzwords.
* Every FAQ answer must be transformative and expert-level, providing real takeaways ABOUT THE SPECIFIC TOPIC.
* If the content is about a specific industry, product, or service, make sure your FAQs reflect that specificity.
`;
}

/**
 * Get the user prompt for the FAQ generator
 */
export function getUserPrompt(domain: string, pageContent: string): string {
  // Extract key information about the content for better context
  const contentLength = pageContent.length;
  const wordCount = pageContent.split(/\s+/).length;
  const contentPreview = pageContent.substring(0, 300).replace(/\s+/g, ' ').trim();
  
  return `
CONTENT ANALYSIS TASK:
Domain: ${domain}
Content Length: ${contentLength} characters (~${wordCount} words)
Content Preview: "${contentPreview}..."

🎯 TASK: Analyze this SPECIFIC content and generate FAQs that are directly relevant to the topics, themes, and information presented. Do NOT generate generic business or security FAQs unless this content is actually about business or security.

CONTENT TO ANALYZE:
${pageContent.substring(0, 8000)}

🚨 CRITICAL REQUIREMENTS:
1. Your FAQs must be SPECIFICALLY about the content above - not generic topics
2. Read and understand the actual subject matter before generating questions
3. Respond ONLY with JSON in the required structure
4. Do not include any additional text, code blocks, or explanations outside of this JSON
5. Make sure all JSON is valid and properly formatted
6. Never use HTML in your response

The JSON structure must be exactly:

{
  "faq": {
    "beginner": [
      {
        "question": "FAQ question for beginners 1",
        "answer": {
          "intro": "A brief introductory sentence:",
          "details": [
            "- Bullet point detail 1.",
            "- Bullet point detail 2.",
            "- Bullet point detail 3."
          ],
          "conclusion": "A concluding sentence summarizing the answer."
        }
      },
      {
        "question": "FAQ question for beginners 2",
        "answer": {
          "intro": "A brief introductory sentence:",
          "details": [
            "- Bullet point detail 1.",
            "- Bullet point detail 2.",
            "- Bullet point detail 3."
          ],
          "conclusion": "A concluding sentence summarizing the answer."
        }
      }
    ],
    "intermediate": [
      {
        "question": "FAQ question for intermediate readers",
        "answer": {
          "intro": "A brief introductory sentence:",
          "details": [
            "- Bullet point detail 1.",
            "- Bullet point detail 2.",
            "- Bullet point detail 3."
          ],
          "conclusion": "A concluding sentence summarizing the answer."
        }
      }
    ],
    "expert": [
      {
        "question": "FAQ question for expert readers",
        "answer": {
          "intro": "A brief introductory sentence:",
          "details": [
            "- Bullet point detail 1.",
            "- Bullet point detail 2.",
            "- Bullet point detail 3."
          ],
          "conclusion": "A concluding sentence summarizing the answer."
        }
      }
    ],
    "company": [
      {
        "question": "FAQ question specifically about the company",
        "answer": {
          "intro": "A brief introductory sentence relevant to the company:",
          "details": [
            "- Bullet point detail 1 regarding the company's approach.",
            "- Bullet point detail 2 regarding its impact or strategy.",
            "- Bullet point detail 3 offering additional context."
          ],
          "conclusion": "A concluding sentence summarizing the company-specific insight."
        }
      }
    ]
  }
}
`;
}
